import React, { useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UsersIcon, CalendarIcon, AcademicCapIcon, PhotoIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { Classroom } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useClassrooms } from '../hooks/useClassrooms';
import { useUsers } from '../hooks/useUsers';
import UserProfileLink from './UserProfileLink';

interface ClassroomCardProps {
  classroom: Classroom;
}

const ClassroomCard: React.FC<ClassroomCardProps> = React.memo(({ classroom }) => {
  const { currentUser } = useAuth();
  const { isAdmin } = useClassrooms();
  const { users, fetchUsers, getUserDisplayName, getUserAvatar } = useUsers();
  
  // Fetch only a small subset of members for display to reduce reads
  useEffect(() => {
    if (classroom.members && classroom.members.length > 0) {
      const subset = classroom.members.slice(0, 5);
      fetchUsers(subset);
    }
  }, [classroom.members, fetchUsers]);
  
  // Memoize date formatting
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Memoize computed values to prevent recalculation
  const { isUserAdmin, hasAdmins, isCreator } = useMemo(() => {
    const isUserAdmin = currentUser && isAdmin(classroom, currentUser.uid);
    const hasAdmins = classroom.admins && Array.isArray(classroom.admins);
    const isCreator = currentUser && classroom.createdBy === currentUser.uid;
    return { isUserAdmin, hasAdmins, isCreator };
  }, [currentUser, classroom, isAdmin]);

  return (
    <Link to={`/app/classrooms/${classroom.id}`}>
      <div className="bg-[#2b2d31] rounded-xl border border-[#1e1f22] p-4 hover:bg-[#313338] transition-all duration-200 hover:border-[#5865f2] group">
        {/* Header with image */}
        <div className="relative mb-4">
          {classroom.profileImageUrl ? (
            <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
              <img 
                src={classroom.profileImageUrl} 
                alt={classroom.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          ) : (
            <div className="w-full h-32 rounded-lg bg-gradient-to-br from-[#5865f2] to-[#4854d4] mb-3 flex items-center justify-center">
              <PhotoIcon className="h-12 w-12 text-white opacity-80" />
            </div>
          )}
          
          <div className="absolute top-2 right-2 flex flex-col space-y-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              classroom.status === 'active' 
                ? 'bg-green-900/50 text-green-300 border border-green-700/50' 
                : 'bg-gray-900/50 text-gray-300 border border-gray-700/50'
            }`}>
              {classroom.status}
            </span>
            
            {isCreator && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-900/50 text-yellow-300 border border-yellow-700/50 flex items-center">
                <StarIcon className="h-3 w-3 mr-1" />
                Creator
              </span>
            )}
            
            {isUserAdmin && !isCreator && (
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-900/50 text-blue-300 border border-blue-700/50 flex items-center">
                <ShieldCheckIcon className="h-3 w-3 mr-1" />
                Admin
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3">
          <div>
            <h3 className="text-lg font-bold text-gray-100 group-hover:text-[#5865f2] transition-colors mb-2">
              {classroom.name}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed line-clamp-2">{classroom.description}</p>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex items-center text-gray-400">
              <AcademicCapIcon className="h-4 w-4 mr-2 text-[#5865f2]" />
              <span className="font-medium">{classroom.category}</span>
            </div>
            
            <div className="flex items-center text-gray-400">
              <CalendarIcon className="h-4 w-4 mr-2 text-[#5865f2]" />
              <span className="font-medium">{classroom.subject}</span>
            </div>
          </div>

          {/* Member count and last updated */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center text-gray-400">
              <UsersIcon className="h-4 w-4 mr-2 text-[#5865f2]" />
              <span className="font-medium">{classroom.memberCount}/{classroom.maxMembers} members</span>
            </div>
            
            <div className="text-xs text-gray-500">
              Updated {formatDate(classroom.updatedAt)}
            </div>
          </div>

          {/* Member avatars */}
          <div className="pt-3 border-t border-[#1e1f22]">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-3">
                  Members
                </span>
                <div className="flex -space-x-2">
                  {(classroom.members || []).slice(0, 5).map((memberId, index) => {
                    const user = users[memberId];
                    const displayName = getUserDisplayName(memberId);
                    const avatar = getUserAvatar(memberId);
                    const isMemberCreator = memberId === classroom.createdBy;
                    
                    return (
                      <div
                        key={memberId}
                        className="relative group"
                        title={`${displayName}${isMemberCreator ? ' (Creator)' : ''}`}
                      >
                        <div onClick={(e) => e.stopPropagation()}>
                          <UserProfileLink 
                            userId={memberId}
                            className="block"
                          >
                          {avatar ? (
                            <img
                              src={avatar}
                              alt={displayName}
                              className="w-8 h-8 rounded-full border-2 border-[#2b2d31] shadow-sm object-cover hover:border-[#5865f2] transition-colors"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5865f2] to-[#4854d4] flex items-center justify-center text-white text-xs font-medium border-2 border-[#2b2d31] shadow-sm hover:border-[#5865f2] transition-colors">
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          )}
                          </UserProfileLink>
                        </div>
                        
                        {/* Star for creator */}
                        {isMemberCreator && (
                          <div className="absolute -top-1 -right-1">
                            <StarIcon className="h-4 w-4 text-yellow-400 drop-shadow-sm" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {classroom.members && classroom.members.length > 5 && (
                    <div className="w-8 h-8 rounded-full bg-[#1e1f22] flex items-center justify-center text-gray-400 text-xs font-medium border-2 border-[#2b2d31] shadow-sm">
                      +{classroom.members.length - 5}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});

export default ClassroomCard; 
